import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { LOGIN_PATH, PRICING_PATH } from "@/lib/auth/auth-paths";
import { LOGO_URL } from "@/lib/constants";
import { useBillingStatusQuery } from "@/hooks/use-billing";
import { useAuthStore } from "@/stores/auth-store";

import { Button } from "@/components/ui/button";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60 * 1000;

// Stripe redirects here after checkout, but the backend learns of the payment
// from a webhook a moment later, so the first status read can still say not
// entitled. Polling keeps a customer who just paid from being told by the
// subscription banner that they have no plan.
export function BillingReturnPage() {
  const { isAuthenticated } = useAuthStore();
  const [isTimedOut, setIsTimedOut] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setIsTimedOut(true), POLL_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, []);

  const billingStatus = useBillingStatusQuery({
    enabled: isAuthenticated,
    refetchInterval: isTimedOut ? false : POLL_INTERVAL_MS,
  });

  if (!isAuthenticated) {
    return <Navigate to={LOGIN_PATH} replace />;
  }

  if (billingStatus.data?.entitled) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-muted/50 px-4 py-12 sm:py-16">
      <div className="mx-auto flex max-w-md flex-col items-center text-center">
        <img src={LOGO_URL} alt="" className="mb-6 h-12 w-auto" />
        {isTimedOut ? (
          <>
            <p className="text-base">
              Your payment went through, but your subscription isn't active
              yet. It can take a few minutes.
            </p>
            <Button
              onClick={() => window.location.reload()}
              className="mt-6 text-base"
            >
              Reload
            </Button>
            <Link
              to={PRICING_PATH}
              className="mt-4 inline-block py-2 text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
            >
              Back to pricing
            </Link>
            <p className="mt-6 text-sm text-muted-foreground">
              Still stuck? Contact us at hi@appio.so.
            </p>
          </>
        ) : (
          <div className="flex items-center gap-3 text-base text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Confirming your subscription…
          </div>
        )}
      </div>
    </div>
  );
}
